// Upload accept/validation. Public API: getUploadAcceptConfig(). Rest are internal.
import {
  ALLOWED_ID_MIME_TYPES,
  ALLOWED_IMAGE_ONLY_MIME_TYPES,
  ALLOWED_TYPES_MODE_ID,
  ALLOWED_TYPES_MODE_IMAGE_ONLY,
  ALLOWED_TYPES_MODE_STANDARD,
  ALLOWED_UPLOAD_MIME_TYPES,
  ALLOWED_VISA_DOCUMENT_EXTRA_MIME_TYPES,
  VISA_DOCUMENT_ID_TYPE,
} from 'constants/upload-allowed-types';
import { parseIdKey } from 'helpers/ids';

const UPLOAD_ALLOWED_TYPES_KEYS = {
  standard: 'uploadAllowedTypes.standard',
  idDocuments: 'uploadAllowedTypes.idDocuments',
  imageOnly: 'uploadAllowedTypes.imageOnly',
  standardOrHtml: 'uploadAllowedTypes.standardOrHtml',
};

// --- Mode / ID helpers (used by MIME and label logic) ---

function isVisaDocumentIdType(idType) {
  if (!idType || typeof idType !== 'string') return false;
  const { idType: type } = parseIdKey(idType);
  return type === VISA_DOCUMENT_ID_TYPE;
}

function getAllowedMimeTypes(mode, idType) {
  if (mode === ALLOWED_TYPES_MODE_IMAGE_ONLY) return ALLOWED_IMAGE_ONLY_MIME_TYPES;
  if (mode === ALLOWED_TYPES_MODE_ID && idType && isVisaDocumentIdType(idType)) {
    return [...ALLOWED_UPLOAD_MIME_TYPES, ...ALLOWED_VISA_DOCUMENT_EXTRA_MIME_TYPES];
  }
  if (mode === ALLOWED_TYPES_MODE_ID) return ALLOWED_ID_MIME_TYPES;
  return ALLOWED_UPLOAD_MIME_TYPES;
}

// --- Mode-based accept and label ---

function getAllowedTypesLabel(mode, idType, t) {
  let key;
  if (mode === ALLOWED_TYPES_MODE_IMAGE_ONLY) key = UPLOAD_ALLOWED_TYPES_KEYS.imageOnly;
  else if (mode === ALLOWED_TYPES_MODE_ID && idType && isVisaDocumentIdType(idType))
    key = UPLOAD_ALLOWED_TYPES_KEYS.standardOrHtml;
  else if (mode === ALLOWED_TYPES_MODE_ID) key = UPLOAD_ALLOWED_TYPES_KEYS.idDocuments;
  else key = UPLOAD_ALLOWED_TYPES_KEYS.standard;
  return t(key);
}

function isAllowedUploadType(fileType, options = {}) {
  const { allowedTypesMode = ALLOWED_TYPES_MODE_STANDARD, idType } = options;
  if (!fileType || typeof fileType !== 'string') return false;
  const list = getAllowedMimeTypes(allowedTypesMode, idType);
  return list.includes(fileType.toLowerCase());
}

function getAcceptAttr(mode, idType) {
  const list = getAllowedMimeTypes(mode, idType);
  const exts = [];
  if (list.some(m => m.includes('pdf'))) exts.push('.pdf');
  if (list.some(m => m.includes('jpeg') || m.includes('jpg'))) exts.push('.jpg', '.jpeg');
  if (list.some(m => m.includes('png'))) exts.push('.png');
  if (list.some(m => m.includes('webp'))) exts.push('.webp');
  if (list.some(m => m.includes('svg'))) exts.push('.svg');
  if (list.some(m => m.includes('avif'))) exts.push('.avif');
  if (list.some(m => m.includes('msword'))) exts.push('.doc');
  if (list.some(m => m.includes('wordprocessingml'))) exts.push('.docx');
  if (list.some(m => m.includes('presentationml'))) exts.push('.pptx');
  if (list.some(m => m.includes('text/html'))) exts.push('.html', '.htm');
  return exts.length ? exts.join(',') : undefined;
}

// --- Custom accept string (when accept prop is a string) ---

function parseAcceptString(acceptString) {
  const extensions = new Set();
  const mimeTypes = new Set();
  const mimePrefixes = new Set();
  if (!acceptString || typeof acceptString !== 'string')
    return { extensions, mimeTypes, mimePrefixes };
  const tokens = acceptString
    .split(',')
    .map(s => s.trim())
    .filter(Boolean);
  for (const token of tokens) {
    if (token.startsWith('.')) {
      extensions.add(token.slice(1).toLowerCase());
    } else if (token.includes('/')) {
      if (token.endsWith('/*')) {
        mimePrefixes.add(token.slice(0, -1).toLowerCase());
      } else {
        mimeTypes.add(token.toLowerCase());
      }
    }
  }
  return { extensions, mimeTypes, mimePrefixes };
}

function isAllowedByAcceptString(fileType, fileName, acceptString) {
  const parsed = parseAcceptString(acceptString);
  const hasAny =
    parsed.extensions.size > 0 || parsed.mimeTypes.size > 0 || parsed.mimePrefixes.size > 0;
  if (!hasAny) return false;

  const type = fileType && typeof fileType === 'string' ? fileType.toLowerCase() : '';
  if (type && parsed.mimeTypes.has(type)) return true;
  if (type && [...parsed.mimePrefixes].some(prefix => type.startsWith(prefix))) return true;

  const ext =
    fileName && typeof fileName === 'string' && fileName.includes('.')
      ? fileName.split('.').pop().toLowerCase()
      : '';
  if (ext && parsed.extensions.has(ext)) return true;
  return false;
}

// --- Composed: effective accept, error label, and file check ---

// Custom accept string wins so dialog, validation, and error label stay in sync.
function getEffectiveAccept(allowedTypesMode, idType, accept) {
  if (typeof accept === 'string') return accept;
  if (allowedTypesMode === ALLOWED_TYPES_MODE_ID) {
    return getAcceptAttr(ALLOWED_TYPES_MODE_ID, idType);
  }
  return getAcceptAttr(allowedTypesMode, idType);
}

function getAllowedLabelForError(allowedTypesMode, idType, accept, t) {
  if (typeof accept === 'string') {
    return t('uploadAllowedTypes.custom', { accept });
  }
  return getAllowedTypesLabel(allowedTypesMode, idType, t);
}

function isFileAllowed(file, options = {}) {
  const { allowedTypesMode = ALLOWED_TYPES_MODE_STANDARD, idType, accept } = options;
  if (!file || typeof file !== 'object') return false;
  if (typeof accept === 'string') {
    return isAllowedByAcceptString(file.type, file.name, accept);
  }
  if (isAllowedUploadType(file.type, { allowedTypesMode, idType })) return true;
  const acceptStr = getAcceptAttr(allowedTypesMode, idType);
  if (acceptStr && file.name) {
    return isAllowedByAcceptString(file.type, file.name, acceptStr);
  }
  return false;
}

// --- Public API ---

export function getUploadAcceptConfig(allowedTypesMode, idType, accept, t) {
  return {
    effectiveAccept: getEffectiveAccept(allowedTypesMode, idType, accept),
    allowedLabel: getAllowedLabelForError(allowedTypesMode, idType, accept, t),
    isFileAllowed: file => isFileAllowed(file, { allowedTypesMode, idType, accept }),
  };
}
